import ArrSVG from '@public/icons/org-arr.svg';
import Image from 'next/image';
import React, { useState } from 'react';
import { Place } from '@/entities/place';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import FoodAreaPopup, { FOOD_AREA_POPUP_NAME } from '@/shared/components/popups/FoodAreaPopup/FoodAreaPopup';
import SchemaPopup from '@/shared/components/popups/SchemaPopup/SchemaPopup';
import Typografed from '@/shared/components/shared/Typografed';
import { ImageShape } from '@/shared/lib/types';

export type FoodAreasListProps = {
    id: number;
    image: ImageShape;
    slug: string;
    name: string;
    access: string;
    format: string;
    description: string;
    images: ImageShape[];
    kitchen?: string;
    operating_mode: { days: string; time: string }[];
    place: Place | null;
    place_id: number;
};

type Props = React.HTMLAttributes<HTMLElement> & {
    foodAreasList: FoodAreasListProps[];
};

const FoodAreasList = ({ foodAreasList }: Props) => {
    const [activeFoodArea, setActiveFoodArea] = useState<FoodAreasListProps | null>(null);

    return (
        <div className="food-areas-list wrapper">
            {foodAreasList.length > 0 ? (
                <ul className="food-areas-list__list list-unstyled">
                    {foodAreasList.map((area) => (
                        <FoodAreasListItem key={area.id} foodArea={area} setActiveFoodArea={setActiveFoodArea} />
                    ))}
                </ul>
            ) : (
                <div className="food-areas-list__empty">
                    <Typografed>Зоны питания не найдены</Typografed>
                </div>
            )}
            <FoodAreaPopup foodArea={activeFoodArea} />
            {activeFoodArea && activeFoodArea.place && (
                <SchemaPopup
                    image={activeFoodArea.place.image}
                    content={{ name: activeFoodArea.place.name, description: activeFoodArea.description }}
                />
            )}
        </div>
    );
};

const FoodAreasListItem = ({
    foodArea,
    setActiveFoodArea,
}: {
    foodArea: FoodAreasListProps;
    setActiveFoodArea: (foodArea: FoodAreasListProps) => void;
}) => {
    const { openPopup } = useSetOpenedPopupsState();
    const { image, name, access, format } = foodArea;

    return (
        <li className="food-areas-list__item">
            <button
                className="food-areas-list__item-btn"
                onClick={() => {
                    setActiveFoodArea(foodArea);
                    openPopup(FOOD_AREA_POPUP_NAME);
                }}
                aria-label="Перейти к зоне питания"
            >
                <span className="food-areas-list__arrow">
                    <ArrSVG />
                </span>
                <div className="food-areas-list__item-image">
                    <Image
                        src={image.src}
                        alt={image.alt ?? ''}
                        width={image.width}
                        height={image.height}
                        sizes="(max-width: 768px) 100vw, (max-width: 1199px) 20vw, 30vw"
                    />
                </div>
                <div className="food-areas-list__item-content">
                    <p className="food-areas-list__item-title text-m">
                        <Typografed>{name}</Typografed>
                    </p>
                    <div className="food-areas-list__item-info">
                        <span className="food-areas-list__item-access-label text-s">Доступ:</span>
                        {access && (
                            <span className="food-areas-list__item-access-value text-s">
                                <Typografed>{access}</Typografed>
                            </span>
                        )}
                        <span className="food-areas-list__item-access-label text-s">Формат:</span>
                        {format && (
                            <span className="food-areas-list__item-access-value text-s">
                                <Typografed>{format}</Typografed>
                            </span>
                        )}
                    </div>
                </div>
            </button>
        </li>
    );
};

export default FoodAreasList;
